
import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import IResource from "@/IResource";
import IOrganisationAvailability from "@/IOrganisationAvailability";
import StepMultiTimeResourceCard from "@/components/organisation/StepMultiTimeResourceCard.vue";

	interface ITimeOption {
		value: number;
		label: string;
		enabled: boolean;
	}

	@Component({
		components: {StepMultiTimeResourceCard}
	})
	export default class StepTime extends Vue {

		@Prop({
			required: true,
			type: Array
		})
		public resources!: IResource[];

		@Prop({
			required: true,
			type: Object
		})
		public availability!: IOrganisationAvailability;

		public fromtime: number = this.$store.state.fromtime;
		public totime: number = this.$store.state.totime;

		public startOptions: {[key: number]: ITimeOption} = {};
		public endOptions: {[key: number]: ITimeOption} = {};

		public validating: boolean = false;

		get notAvailableResources(): IResource[] {
			let notAvailable: IResource[] = [];
			let reservationStart = new Date(this.dateStart);
			reservationStart.setMinutes(reservationStart.getMinutes() + this.fromtime);
			this.resources.forEach(resource => {
				// check for min_hours_to_reservation
				let minDate = new Date();
				minDate.setHours(minDate.getHours() + resource.min_hours_to_reservation);
				if (minDate.getTime() > reservationStart.getTime()) {
					if (notAvailable.indexOf(resource) < 0) {
						notAvailable.push(resource);
					}
				}

				// check for max_hours_to_reservation
				if (resource.max_hours_to_reservation) {
					let maxDate = new Date();
					maxDate.setHours(maxDate.getHours() + resource.max_hours_to_reservation);
					if (maxDate.getTime() < reservationStart.getTime()) {
						if (notAvailable.indexOf(resource) < 0) {
							notAvailable.push(resource);
						}
					}
				}

				// check starttime/endtime
				if (resource.starttime * 60 > this.fromtime || resource.endtime * 60 < this.totime) {
					if (notAvailable.indexOf(resource) < 0) {
						notAvailable.push(resource);
					}
				}

				// check availability
				if (this.availability[resource.id] && this.availability[resource.id][this.dateKeyStart]) {
					this.availability[resource.id][this.dateKeyStart].forEach(slot => {
						if (slot[0] < this.totime && slot[1] > this.fromtime) {
							if (notAvailable.indexOf(resource) < 0) {
								notAvailable.push(resource);
							}
						}
					})
				}

				// check max duration
				if (resource.max_duration > 0 && this.totime - this.fromtime > resource.max_duration) {
					if (notAvailable.indexOf(resource) < 0) {
						notAvailable.push(resource);
					}
				}
			})
			return notAvailable;
		}

		get availableResources(): IResource[] {
			return this.resources.filter(resource => this.notAvailableResources.indexOf(resource) < 0);
		}

		get fromtimeError(): boolean {
			return this.validating && this.fromtime === 0;
		}

		get totimeError(): boolean {
			return this.validating && (this.totime === 0 || !(this.totime in this.filteredEndOptions));
		}

		get mintime(): Date {
			// get minimum min_hours_to_reservation from all resources
			let min_date = new Date();
			let min_hours_to_reservation: number = 87600;
			if (this.resources) {
				this.resources.forEach((resource: IResource) => {
					if (resource.min_hours_to_reservation < min_hours_to_reservation) {
						min_hours_to_reservation = resource.min_hours_to_reservation;
					}
				})
			}
			min_date.setHours(min_date.getHours() + min_hours_to_reservation);
			return min_date;
		}

		get maxtime(): Date {
			// get maximum max_hours_to_reservation from all resources
			let max_date = new Date();
			let max_hours_to_reservation: number = 0;
			if (this.resources) {
				this.resources.forEach((resource: IResource) => {
					if (resource.max_hours_to_reservation && resource.max_hours_to_reservation > max_hours_to_reservation) {
						max_hours_to_reservation = resource.max_hours_to_reservation;
					} else if (resource.max_hours_to_reservation === 0) {
						max_hours_to_reservation = 87600;
					}
				})
			}
			max_date.setHours(max_date.getHours() + max_hours_to_reservation);
			return max_date;
		}

		get starttime(): number {
			// get earliest starttime from all resources
			let starttime: number = 24;
			if (this.resources) {
				this.resources.forEach((resource: IResource) => {
					if (resource.starttime < starttime) {
						starttime = resource.starttime;
					}
				})
			}
			return starttime;
		}

		get endtime(): number {
			// get latest endtime from all resources
			let endtime: number = 0;
			if (this.resources) {
				this.resources.forEach((resource: IResource) => {
					if (resource.endtime > endtime) {
						endtime = resource.endtime;
					}
				})
			}
			return endtime;
		}

		get slot_size(): number {
			// get minimum slot_size from all resources
			let slot_size: number = 1440;
			if (this.resources) {
				this.resources.forEach((resource: IResource) => {
					if (resource.slot_size < slot_size) {
						slot_size = resource.slot_size;
					}
				})
			}
			return slot_size;
		}

		get isSameDay(): boolean {
			return this.dateKeyEnd === this.dateKeyStart;
		}

		get dateStart(): Date {
			return this.$store.state.startdate;
		}

		get dateEnd(): Date {
			if (this.$store.state.enddate === null) {
				return this.$store.state.startdate;
			}
			return this.$store.state.enddate;
		}

		get dateKeyStart(): string {
			let d = this.dateStart;
			return d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
		}

		get dateKeyEnd(): string {
			let d = this.dateEnd;
			return d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
		}

		get filteredEndOptions(): {[key: number]: ITimeOption} {
			let options: {[key: number]: ITimeOption} = {};
			let disabledOptionFound = false;
			Object.keys(this.endOptions).forEach(keyStr => {
				const key: number = parseInt(keyStr, 10);
				if (this.dateKeyStart !== this.dateKeyEnd || this.endOptions[key].value > this.fromtime) {
					if (!disabledOptionFound) {
						options[key] = JSON.parse(JSON.stringify(this.endOptions[key]));
						if (!options[key].enabled) {
							options[key].enabled = true; // make starttime of next reservation selectable as endtime
						}
					}
					if (!this.endOptions[key].enabled) {
						disabledOptionFound = true;
					}
				}
			});

			return options;
		}

		public mounted() {
			this.buildTimeOptions();
		}

		@Watch('availability')
		public watchAvailability() {
			this.buildTimeOptions();
		}

		public buildTimeOptions() {
			let minminutes: number = 0;
			if (this.dateStart < this.mintime) {
				minminutes = this.mintime.getHours() * 60 + this.mintime.getMinutes();
			}
			let start = this.starttime * 60;
			let end = this.endtime * 60;
			let step = this.slot_size;

			let minutes;
			for (minutes = start; minutes <= end; minutes += step) {
				let mins: number|string = minutes % 60;
				if (mins < 10) {
					mins = "0" + mins;
				}
				let hours: number|string = Math.floor(minutes / 60);
				if (hours < 10) {
					hours = "0" + hours;
				}
				if (minutes > minminutes) {
					if (minutes < end) {
						let dataStart: ITimeOption = {
							value: minutes,
							label: hours + ':' + mins,
							enabled: true
						};
						this.$set(this.startOptions, minutes, dataStart);
					}
				}
				let dataEnd: ITimeOption = {
					value: minutes,
					label: hours + ':' + mins,
					enabled: true
				};
				if (this.dateStart.toDateString() !== this.dateEnd.toDateString()) {
					this.$set(this.endOptions, minutes, dataEnd);
				} else if (minutes > minminutes && minutes > start) {
					this.$set(this.endOptions, minutes, dataEnd);
				}
			}
		}

		public formattedDuration(start: number, end: number): string {
			if (!this.isSameDay || this.fromtime === 0) {
				return '';
			}
			let duration: string = '';
			let diff = end - start;
			if (start < end) {
				if (diff >= 60) {
					duration += Math.floor(diff / 60) + ':';
					duration += (Math.round(diff % 60) + '').padStart(2, '0') + 'h';
				} else {
					duration = diff + 'min'
				}
				return '  -  ' + duration;
			}
			return '';
		}

		public selectResource(id: number) {
			if (this.fromtime === 0 || this.totime === 0 || !(this.totime in this.filteredEndOptions)) {
				this.validating = true;
			} else {
				this.validating = false;
				this.$emit('selectresource', { fromtime: this.fromtime, totime: this.totime, id: id})
			}
		}
	}
